import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { loadQuestions } from "../lib/api";
import type { Theme } from "../lib/types";

export default function Home() {
  const [themes, setThemes] = useState<Theme[]>([]);

  useEffect(() => {
    void loadQuestions().then((d) => setThemes(d.themes));
  }, []);

  return (
    <div className="space-y-12">
      <section>
        <h1 className="text-4xl font-semibold leading-tight max-w-prose">
          Rebuild Lebanon — 100 questions, by the people, for the people.
        </h1>
        <p className="mt-6 max-w-prose text-lg text-ink/80">
          A country normally outsources its biggest decisions to a head of state, a
          parliament, or a constitutional drafting committee. Here, every one of those
          decisions is surfaced as a public vote — with the historical context,
          the findings, and the trade-offs explained.
        </p>
        <p className="mt-4 max-w-prose text-lg text-ink/80">
          Anonymous voting. One vote per question. Cohort-filterable results. Lebanon
          first; the framework is generic to any country.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/themes" className="btn-primary">
            Start voting
          </Link>
          <Link to="/results" className="btn-ghost">
            See results
          </Link>
          <Link to="/about" className="btn-ghost">
            About
          </Link>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-4">The 10 themes</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {themes.map((t) => (
            <Link
              key={t.id}
              to={`/themes/${t.slug}`}
              className="card hover:border-cedar transition no-underline"
            >
              <div className="flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-wider text-muted">
                  Theme {t.id}
                </span>
                <span className="text-xs text-muted">
                  Q{t.questionRange[0]}–Q{t.questionRange[1]}
                </span>
              </div>
              <h3 className="mt-1 font-medium text-ink">{t.title}</h3>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-t border-ink/10 pt-8">
        <h2 className="text-xl font-semibold mb-2">How this works</h2>
        <ul className="space-y-2 text-ink/80 max-w-prose list-disc pl-5">
          <li>Register with your email and a chosen username. We send you a magic link to verify.</li>
          <li>Vote once on each of the 100 questions. You can change your vote at any time.</li>
          <li>Your username and email are never tied to your votes in any public response.</li>
          <li>
            Filter the results by residency, age, region, or (optionally) self-disclosed
            sect — with a minimum-bucket rule that suppresses breakdowns below 10 votes.
          </li>
          <li>No founder vote, no editorial recommendation, no expert prior. The platform is neutral civic infrastructure.</li>
        </ul>
        <p className="mt-4 text-sm text-muted">
          v0: email-based registration is Sybil-vulnerable. Results are consultative,
          not constitutionally binding. We will publish identity-verification limits
          honestly on the About page.
        </p>
      </section>
    </div>
  );
}
